import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Badge,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';

import { RootState, AppDispatch } from '../store/index';
import { logout } from '../store/slices/authSlice';
import { toggleSidebar } from '../store/slices/uiSlice';
import { Sidebar } from './Sidebar';
import { NotificationPanel } from './NotificationPanel';
import { useLegislationNotifications } from '../hooks/useLegislationNotifications';
import { AppBreadcrumb } from './layout/AppBreadcrumb';
import { getPageTitleFromPath } from '../utils/pageBreadcrumb';
import { loadSsoNav, clearSsoNav, safeExternalUrl } from '../utils/ssoNav';
import logoVerde from '../assets/brand/logo-verde.png';
import logoAzul from '../assets/brand/logo-azul.png';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  
  const { user } = useSelector((state: RootState) => state.auth);
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  const { unreadCount } = useLegislationNotifications(!!user?.id);

  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  // Origem do SSO: define a logo do botão de voltar e as URLs de retorno.
  const nav = loadSsoNav();
  const isAzul = nav.origin === 'azul';
  const originLogo = isAzul ? logoAzul : logoVerde;
  const originNome = isAzul ? 'Plataforma Azul' : 'Plataforma Verde';
  const homeUrl = safeExternalUrl(nav.home_url); // voltar → origem
  const pvUrl = safeExternalUrl(nav.pv_url); // perfil + sair → sempre PV
  const perfilUrl = pvUrl && nav.user_id ? `${pvUrl}/gerador/usuario/${nav.user_id}/editar` : null;

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  // Voltar: apenas navega de volta à origem (não encerra a sessão).
  const handleVoltar = () => {
    if (homeUrl) window.location.href = homeUrl;
  };

  // Perfil: editar o cadastro do usuário na PV (a conta é gerida lá).
  const handlePerfil = () => {
    if (perfilUrl) window.location.href = perfilUrl;
  };

  // Sair: encerra a sessão do EnvWatch e volta para a PV.
  const handleLogout = () => {
    dispatch(logout());
    clearSsoNav();
    if (pvUrl) window.location.href = pvUrl;
  };

  const pageTitle = getPageTitleFromPath(location.pathname);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Sidebar open={sidebarOpen} onClose={() => dispatch(toggleSidebar())} />

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            boxShadow: 'none',
          }}
        >
          <Toolbar sx={{ minHeight: 64, px: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={() => dispatch(toggleSidebar())}
              edge="start"
              sx={{
                mr: 0,
                bgcolor: 'rgba(255,255,255,0.14)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <AppBreadcrumb pageTitle={pageTitle} />
            </Box>

            <IconButton color="inherit" onClick={handleNotificationOpen} sx={{ mr: 0.5 }}>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Voltar para a origem (PV ou H2O), com a logo correspondente. */}
            {homeUrl && (
              <Tooltip title={`Voltar para a ${originNome}`}>
                <IconButton
                  aria-label={`voltar para a ${originNome}`}
                  onClick={handleVoltar}
                  sx={{
                    mr: 0.5,
                    p: 0.5,
                    bgcolor: '#fff',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
                  }}
                >
                  <Box
                    component="img"
                    src={originLogo}
                    alt={originNome}
                    sx={{ width: 30, height: 30, display: 'block', objectFit: 'contain' }}
                  />
                </IconButton>
              </Tooltip>
            )}

            {/* Perfil: avatar leva ao cadastro do usuário na PV. */}
            {perfilUrl && (
              <Tooltip title="Editar meu perfil">
                <IconButton
                  aria-label="editar perfil"
                  onClick={handlePerfil}
                  color="inherit"
                  sx={{ mr: 0.5 }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.18)' }}>
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
            )}

            {/* Sair: encerra a sessão e volta para a PV. */}
            <Tooltip title="Sair">
              <IconButton
                aria-label="sair"
                onClick={handleLogout}
                color="inherit"
                sx={{ ml: 0.5 }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        <NotificationPanel
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
        />

        <Box
          sx={{
            flexGrow: 1,
            px: 2.5,
            pt: 9.5,
            pb: 3.75,
            backgroundColor: 'background.default',
            minHeight: '100vh',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
